import type { FC } from 'react';

import { Placeholder, useCms, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { CircledNumber } from '@eneco/components/ui/CircledNumber/CircledNumber';
import { useFormatter } from '@eneco/i18n';
import { Link } from '@eneco/next';
import {
  Badge,
  Box,
  ButtonLink,
  Card,
  Grid,
  Heading,
  IconLoader,
  Image,
  Ribbon as SparkyRibbon,
  Stack,
  Stretch,
  Text,
  TextLink,
  VisuallyHidden,
} from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { useI18nTranslation } from '@eneco/sparky/providers';
import type { BoxProps, DynamicVariantProp, HeadingProps } from '@eneco/sparky/types';

import type { ContentCardContent } from './content-card-content.types';
import { CONTENT_CARD_SLOTS, resolveSlot } from './slots';

type MapImage = ReturnType<typeof useImageMapper>;
type LinkCta = ReturnType<typeof linkWithUrl>;

type ContentCardImageSectionProps = {
  imageProps: ReturnType<MapImage>;
  isCompactCard: boolean;
  isCompactOnMobile: boolean;
  isCompactOnDesktop: boolean;
  objectFit: DynamicVariantProp<'objectFit'>;
  cardNumber: ContentCardContent['cardNumber'];
  promoSlot: string | undefined;
};

/** Image, optional card-number overlay and the `jss-promo-box` slot shown above the text section. */
const ContentCardImageSection: FC<ContentCardImageSectionProps> = ({
  imageProps,
  isCompactCard,
  isCompactOnMobile,
  isCompactOnDesktop,
  objectFit,
  cardNumber,
  promoSlot,
}) => (
  <Stack.Item shrink={false}>
    <Grid gridTemplateColumns="40px auto 1fr" gridTemplateRows="20px auto 1fr">
      <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
        <Image
          alt={imageProps?.alt ?? ''}
          width={{ initial: isCompactOnMobile ? '80px' : '100%', md: isCompactOnDesktop ? '160px' : '100%' }}
          height={{ initial: isCompactOnMobile ? '80px' : 'auto', md: isCompactOnDesktop ? '160px' : 'auto' }}
          src={imageProps?.src ?? ''}
          srcSet={imageProps?.srcSet ?? ''}
          sizes={{ initial: isCompactOnMobile ? '80px' : '100%', md: isCompactOnDesktop ? '160px' : '100%' }}
          objectFit={objectFit}
          aspectRatio="16/9"
        />
      </Grid.Item>
      {cardNumber && !isCompactCard && (
        <Grid.Item aria-hidden="true" gridColumn="2 / 2" gridRow="2 / 2">
          <CircledNumber>{cardNumber}</CircledNumber>
        </Grid.Item>
      )}
      <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
        {promoSlot ? <Placeholder name={promoSlot} /> : null}
      </Grid.Item>
    </Grid>
  </Stack.Item>
);

type ContentCardTitleSectionProps = {
  title: ContentCardContent['title'];
  badgeText: ContentCardContent['badgeText'];
  cardNumber: ContentCardContent['cardNumber'];
  headingLevel: HeadingProps['as'];
  isCompactCard: boolean;
};

const ContentCardTitleSection: FC<ContentCardTitleSectionProps> = ({
  title,
  badgeText,
  cardNumber,
  headingLevel,
  isCompactCard,
}) => {
  const step = useI18nTranslation('step');
  const { format } = useFormatter();

  return (
    <Stack.Item>
      <Stack gap={isCompactCard ? '1' : '2'}>
        {title && (
          <Heading as={headingLevel} size={{ initial: 'XS', md: 'S' }}>
            {cardNumber && <VisuallyHidden>{format(step, { step: cardNumber })}: </VisuallyHidden>}
            {title}
          </Heading>
        )}
        {badgeText && (
          <Stack.Item>
            <Badge size="M">{badgeText}</Badge>
          </Stack.Item>
        )}
      </Stack>
    </Stack.Item>
  );
};

type ContentCardCtasProps = {
  buttonCta: LinkCta;
  textCta: LinkCta;
  hasBackgroundColor: boolean;
};

const ContentCardCtas: FC<ContentCardCtasProps> = ({ buttonCta, textCta, hasBackgroundColor }) => (
  <>
    {buttonCta?.text && (
      <Stack.Item>
        <Stretch width={{ initial: true, md: false }}>
          <Link linkType={buttonCta.linkType ?? ''} linkValue={{ href: buttonCta.href, target: buttonCta.target }}>
            <ButtonLink target={buttonCta.target}>{buttonCta.text}</ButtonLink>
          </Link>
        </Stretch>
      </Stack.Item>
    )}
    {textCta?.text && (
      <Stack.Item>
        <Link linkType={textCta.linkType ?? ''} linkValue={{ href: textCta.href, target: textCta.target }}>
          <TextLink
            target={textCta.target}
            {...(hasBackgroundColor ? { color: 'textOnBackgroundVarOne', emphasis: 'low' } : { emphasis: 'high' })}>
            {textCta.text}
          </TextLink>
        </Link>
      </Stack.Item>
    )}
  </>
);

type ContentCardTextSectionProps = {
  content: ContentCardContent;
  richText: string | undefined;
  isElevated: boolean;
  isCompactCard: boolean;
  hasImage: boolean;
  hasBackgroundColor: boolean;
  headingLevel: HeadingProps['as'];
  mainSlot: string | undefined;
  buttonCta: LinkCta;
  textCta: LinkCta;
};

/** Title/badge, body text, main slot and CTAs — only rendered when `hasVisibleTextSection` is true. */
const ContentCardTextSection: FC<ContentCardTextSectionProps> = ({
  content,
  richText,
  isElevated,
  isCompactCard,
  hasImage,
  hasBackgroundColor,
  headingLevel,
  mainSlot,
  buttonCta,
  textCta,
}) => (
  <Stretch>
    <Box
      paddingX={isElevated && !isCompactCard ? '6' : '0'}
      paddingTop={(isElevated || hasImage) && !isCompactCard ? '6' : '0'}
      paddingBottom={isElevated && !isCompactCard ? '6' : '0'}>
      <Stretch>
        <Stack gap={isCompactCard ? '2' : '6'}>
          {(content.title || content.badgeText) && (
            <ContentCardTitleSection
              title={content.title}
              badgeText={content.badgeText}
              cardNumber={content.cardNumber}
              headingLevel={headingLevel}
              isCompactCard={isCompactCard}
            />
          )}
          <Stack.Item grow>
            <Stack gap={isCompactCard ? '2' : '6'}>
              {richText && (
                <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                  <RichText html={richText} />
                </Text>
              )}
              {mainSlot ? <Placeholder name={mainSlot} /> : null}
            </Stack>
          </Stack.Item>
          <ContentCardCtas buttonCta={buttonCta} textCta={textCta} hasBackgroundColor={hasBackgroundColor} />
        </Stack>
      </Stretch>
    </Box>
  </Stretch>
);

/**
 * `ContentCard` block for the CMS-agnostic lane, serving **both** providers.
 *
 * `buttonLink`/`link` carry their own display text on stored content, even though the generated schema
 * does not declare it (the dataset is schemaless). `jss-promo-box` has no Sanity-side field on
 * `ContentCard` itself (unlike `SplitView`, which does model it), so that slot always renders empty
 * there.
 *
 * Known simplification: `showRibbonPlaceholder`/`variant` are not fields on `ContentCard` — the legacy
 * component receives them as JSX props from a *parent* component (e.g. a carousel rendering several
 * cards with a shared invisible-ribbon spacer). The CMS-agnostic registry gives every component nothing
 * but its own content (`ComponentsDefinition`'s contract, `types.ts`) — there is no channel for a parent
 * to pass configuration down — so both always take their default (`showRibbonPlaceholder=false`,
 * `variant='default'`).
 *
 * Drops the Sitecore Experience Editor `wrap()` calls — this lane never runs inside the Experience
 * Editor.
 */
const ContentCard: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ContentCardContent>();
  const rendering = useRendering();
  const mapImage = useImageMapper();
  const isDesktop = useMediaQuery('md');
  // `'1'` from a Sitecore rendering parameter, as the legacy component checks; `'true'` from Sanity's boolean.
  const elevation = useSetting('setElevation');
  const isElevated = elevation === '1' || elevation === 'true';
  const isCompactOnMobile = useSetting('mobileVariant') === 'compact';
  const isCompactOnDesktop = useSetting('desktopVariant') === 'compact';
  const isCompactCard = (isDesktop && isCompactOnDesktop) || (!isDesktop && isCompactOnMobile);
  const backgroundColor = useSetting('backgroundColor') as BoxProps['backgroundColor'];
  const hasBackgroundColor = Boolean(backgroundColor);
  const headingLevel = (useSetting('headingLevel') || 'h3') as HeadingProps['as'];
  const objectFit = (useSetting('objectFit') ?? 'cover') as DynamicVariantProp<'objectFit'>;

  const imageProps = mapImage(content.image);
  const hasImage = Boolean(imageProps?.src);
  const richText = adapter.toRichText(content.content);

  const promoSlot = resolveSlot(rendering, CONTENT_CARD_SLOTS.promo);
  // Two distinct fields, not a fallback pair: `buttonLink` is the primary CTA (a `ButtonLink`),
  // `link` a secondary one (a `TextLink`) — both can render at once, as siblings.
  const buttonCta = linkWithUrl(content.buttonLink);
  const textCta = linkWithUrl(content.link);
  const mainSlot = resolveSlot(rendering, CONTENT_CARD_SLOTS.main);

  const hasVisibleTextSection = Boolean(
    content.title || content.badgeText || richText || buttonCta?.text || textCta?.text,
  );

  const cardBody = (
    <Stretch>
      <Stack
        direction={isCompactCard ? 'row' : 'column'}
        gap={{ initial: isCompactCard ? '3' : '0', md: isCompactCard ? '6' : '0' }}>
        {hasImage && (
          <ContentCardImageSection
            imageProps={imageProps}
            isCompactCard={isCompactCard}
            isCompactOnMobile={isCompactOnMobile}
            isCompactOnDesktop={isCompactOnDesktop}
            objectFit={objectFit}
            cardNumber={content.cardNumber}
            promoSlot={promoSlot}
          />
        )}
        {hasVisibleTextSection && (
          <ContentCardTextSection
            content={content}
            richText={richText}
            isElevated={isElevated}
            isCompactCard={isCompactCard}
            hasImage={hasImage}
            hasBackgroundColor={hasBackgroundColor}
            headingLevel={headingLevel}
            mainSlot={mainSlot}
            buttonCta={buttonCta}
            textCta={textCta}
          />
        )}
      </Stack>
    </Stretch>
  );

  if (!isElevated) {
    return <Stack>{cardBody}</Stack>;
  }

  return (
    <Stack>
      <Stretch>
        <Card overflow="hidden">
          {content.ribbonText && <ContentCardRibbon icon={content.ribbonIcon} text={content.ribbonText} />}
          <Stretch>
            <Box backgroundColor={hasBackgroundColor ? backgroundColor : 'backgroundPrimary'}>
              {isCompactCard ? <Box padding="6">{cardBody}</Box> : cardBody}
            </Box>
          </Stretch>
        </Card>
      </Stretch>
    </Stack>
  );
};

const ContentCardRibbon: FC<{ icon?: string; text: string }> = ({ icon, text }) => (
  <SparkyRibbon>
    <Stack direction="row" alignY="center" gap="2" alignX="center">
      {icon && (
        <Stack.Item>
          <IconLoader iconName={icon} color="iconInverted" size="small" />
        </Stack.Item>
      )}
      <Stack.Item>
        <Text display="inline">{text}</Text>
      </Stack.Item>
    </Stack>
  </SparkyRibbon>
);

export default ContentCard;

